import { CSSProperties, FC } from 'react';
import cn from 'bem-cn';

import { isAnimatedIcon, getBaseIconName } from './Icon.helpers';
import { IconProps } from './Icon.types';
import { useAnimatedIconControl } from './Icon.hooks';
import './Icon.scss';

// Импорты всех иконок как React компонентов через ?inline
import ai from './icons/ai.svg?inline';
import aliceLogo from './icons/alice_logo.svg?inline';
import alice from './icons/alice.svg?inline';
import application from './icons/application.svg?inline';
import arrowDown from './icons/arrow_down.svg?inline';
import chevronDown from './icons/chevron_down.svg?inline';
import chevronRight from './icons/chevron_right.svg?inline';
import close from './icons/close.svg?inline';
import compete from './icons/compete.svg?inline';
import copy from './icons/copy.svg?inline';
import crown from './icons/crown.svg?inline';
import eye from './icons/eye.svg?inline';
import info from './icons/info.svg?inline';
import inspiration from './icons/inspiration.svg?inline';
import learn from './icons/learn.svg?inline';
import like from './icons/like.svg?inline';
import logo from './icons/logo.svg?inline';
import menuBento from './icons/menu_bento.svg?inline';
import more from './icons/more.svg?inline';
import person from './icons/person.svg?inline';
import plus from './icons/plus.svg?inline';
import search from './icons/search.svg?inline';
import share from './icons/share.svg?inline';
import smallTick from './icons/small_tick.svg?inline';
import stars from './icons/stars.svg?inline';
import tick from './icons/tick.svg?inline';
import time from './icons/time.svg?inline';
import trash from './icons/trash.svg?inline';
import checkboxTick from './icons/checkbox_tick.svg?inline';
import backArrow from './icons/back_arrow.svg?inline';
import reset from './icons/reset.svg?inline';
import heart from './icons/heart.svg?inline';
import heartFilled from './icons/heart_filled.svg?inline';
import edit from './icons/edit.svg?inline';
import write from './icons/write.svg?inline';
import trophy from './icons/trophy.svg?inline';
import terminal from './icons/terminal.svg?inline';
import rating from './icons/rating.svg?inline';
import prompt from './icons/prompt.svg?inline';
import play from './icons/play.svg?inline';
import thinking from './icons/thinking.svg?inline';
import picture from './icons/picture.svg?inline';
import faq from './icons/faq.svg?inline';
import smile from './icons/smile.svg?inline';
import aliceOutline from './icons/alice_outline.svg?inline';

// Импорты анимированных иконок
import competeAnimated from './icons/animated/compete.svg?inline';
import inspirationAnimated from './icons/animated/inspiration.svg?inline';
import learnAnimated from './icons/animated/learn.svg?inline';

// Объект с маппингом имен на компоненты
const icons = {
    ai,
    aliceLogo,
    alice,
    application,
    arrowDown,
    chevronDown,
    chevronRight,
    close,
    compete,
    copy,
    crown,
    eye,
    info,
    inspiration,
    learn,
    like,
    logo,
    menuBento,
    more,
    person,
    plus,
    search,
    share,
    smallTick,
    stars,
    tick,
    time,
    trash,
    checkboxTick,
    backArrow,
    reset,
    heart,
    heartFilled,
    edit,
    write,
    trophy,
    rating,
    terminal,
    prompt,
    play,
    thinking,
    picture,
    competeAnimated,
    inspirationAnimated,
    learnAnimated,
    faq,
    smile,
    aliceOutline,
} as const;

const b = cn('icon');

export const Icon: FC<IconProps> = ({
    name,
    className,
    fillColor,
    strokeColor,
    rotate,
    onClick,
    onMouseDown,
    size,
}) => {
    const isAnimatedIconName = isAnimatedIcon(name);

    const { ref: spanRef, shouldShowAnimated } = useAnimatedIconControl(isAnimatedIconName);

    const iconClasses = b({
        clickable: onClick ? true : null,
        'with-fill': fillColor ? true : null,
        'with-stroke': strokeColor ? true : null,
    });

    const iconStyle: CSSProperties = {
        ...(fillColor && ({ '--icon-fill-color': fillColor } as CSSProperties)),
        ...(strokeColor && ({ '--icon-stroke-color': strokeColor } as CSSProperties)),
        ...(size && ({ '--icon-size': `${size}px` } as CSSProperties)),
        ...(rotate && ({ transform: `rotate(${rotate}deg)` } as CSSProperties)),
    };

    const iconNameToRender = isAnimatedIconName && !shouldShowAnimated ? getBaseIconName(name) : name;
    const IconComponent = icons[iconNameToRender];

    if (!IconComponent) {
        console.warn(`Icon "${iconNameToRender}" not found`);

        return null;
    }

    return (
        <span
            ref={spanRef}
            className={`${iconClasses} ${className}`}
            style={iconStyle}
            onClick={onClick}
            onMouseDown={onMouseDown}
        >
            <IconComponent />
        </span>
    );
};
